import axios from 'axios';

// Base URL for API requests
const deriveApiBase = () => {
  const prodDefault = 'https://api.autolistify.com';

  // In production, always use the production API URL
  if (process.env.NODE_ENV === 'production') {
    return `${prodDefault}/api`;
  }

  // In development, allow override via env vars
  const fromUrl = process.env.REACT_APP_API_URL;
  const fromBase = process.env.REACT_APP_API_BASE_URL;
  const chosen = fromUrl || fromBase || 'http://localhost:3001';
  const noTrailing = String(chosen).replace(/\/$/, '');
  return noTrailing.endsWith('/api') ? noTrailing : `${noTrailing}/api`;
};

const API_BASE_URL = deriveApiBase();

// Configure axios instance with CORS support
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false,
  headers: {
    'Accept': 'application/json',
  },
  // Increase timeout for large requests
  timeout: 60000, // 60 seconds
});

// Add a request interceptor to log all requests
api.interceptors.request.use(
  config => {
    console.log('Sending request to:', config.url);
    return config;
  },
  error => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Add a response interceptor to log all responses
api.interceptors.response.use(
  response => {
    console.log('Response received:', response.config.url, response.status);
    return response;
  },
  error => {
    console.error('Response error:', error.message);
    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
      console.error('Response headers:', error.response.headers);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error:', error.message);
    }
    return Promise.reject(error);
  }
);

// Log environment for debugging
console.log('Environment:', {
  apiBaseUrl: API_BASE_URL,
  nodeEnv: process.env.NODE_ENV,
  envVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_'))
});

/**
 * Create a 3D model from an image using Replicate's API
 * @param {Object} options - Generation options
 * @param {string} options.image - Image URL for image-to-3d conversion
 * @param {number} [options.octree_resolution=256] - Octree resolution (128-512)
 * @param {number} [options.inference_steps=20] - Number of inference steps (10-50)
 * @param {boolean} [options.remove_background=true] - Whether to remove background
 * @returns {Promise<Object>} - Prediction result
 */

export const create3DModel = async ({
  image,
  octree_resolution = 256,
  inference_steps = 20,
  remove_background = true
} = {}) => {
  const requestId = Date.now().toString(36) + Math.random().toString(36).substr(2);

  try {
    console.log(`[${requestId}] Starting 3D model generation`);

    if (!image) {
      throw new Error('Image is required for 3D model generation');
    }

    // Prepare the payload as JSON
    const payload = {
      image: image, // Send the base64 string directly
      octree_resolution: octree_resolution,
      inference_steps: inference_steps,
      remove_background: remove_background,
    };

    console.log(`[${requestId}] Sending JSON payload:`, payload);

    const apiUrl = `${API_BASE_URL}/ai3d`;
    console.log(`[${requestId}] Sending request to:`, apiUrl);

    try {
      console.log(`[${requestId}] Sending fetch request...`);
      const response = await fetch(apiUrl, {
        method: 'POST',
        // Set Content-Type to application/json
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload), // Stringify the JSON payload
        mode: 'cors',
        credentials: 'include',
        cache: 'no-cache',
      });

      console.log(`[${requestId}] Fetch completed, status:`, response.status);

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.text();
          try {
            errorData = JSON.parse(errorData);
          } catch (e) {
            // Not JSON, use as is
          }
        } catch (e) {
          errorData = { message: 'Failed to parse error response' };
        }

        console.error(`[${requestId}] Request failed with status ${response.status}:`, errorData);
        throw new Error(errorData.message || `Request failed with status ${response.status}`);
      }

      const data = await response.json();
      console.log(`[${requestId}] Prediction started:`, data);
      return data;
    } catch (fetchError) {
      console.error(`[${requestId}] Fetch error:`, fetchError);
      if (fetchError.name === 'TypeError' && fetchError.message === 'Failed to fetch') {
        throw new Error('Failed to connect to the server. Please check your internet connection and try again.');
      }
      throw fetchError;
    }
  } catch (error) {
    console.error('Error in create3DModel:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });

    if (error.message.includes('Network Error')) {
      throw new Error('Failed to connect to the server. Please check your internet connection.');
    }

    throw new Error(error.response?.data?.error || 'Failed to create 3D model');
  }
};

/**
 * Get the status of a prediction
 * @param {string} predictionId - The prediction ID
 * @returns {Promise<Object>} - Prediction status and output if available
 */
export const get3DModelStatus = async (predictionId) => {
  try {
    const response = await api.get(`/ai3d/status/${predictionId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting 3D model status:', error);
    throw new Error(error.response?.data?.error || 'Failed to get 3D model status');
  }
};

/**
 * Poll for prediction completion
 * @param {string} predictionId - The prediction ID
 * @param {number} [interval=2000] - Polling interval in ms
 * @returns {Promise<Object>} - Completed prediction with output
 */
export const pollFor3DModel = async (predictionId, interval = 2000) => {
  return new Promise((resolve, reject) => {
    const checkStatus = async () => {
      try {
        const status = await get3DModelStatus(predictionId);

        if (status.status === 'succeeded' || status.status === 'failed' || status.status === 'canceled') {
          resolve(status);
          return;
        }

        // If not done, check again after interval
        setTimeout(checkStatus, interval);
      } catch (error) {
        reject(error);
      }
    };

    // Start polling
    checkStatus();
  });
};

/**
 * Generate a 3D model from an image and wait for completion
 * @param {Object} options - Generation options
 * @param {string} options.image - Image URL for image-to-3d conversion
 * @param {number} [options.octree_resolution=256] - Octree resolution (128-512)
 * @param {number} [options.inference_steps=20] - Number of inference steps (10-50)
 * @param {boolean} [options.remove_background=true] - Whether to remove background
 * @param {number} [pollInterval=2000] - Polling interval in ms
 * @returns {Promise<Object>} - Completed prediction with output
 */
export const generate3DModel = async (options, pollInterval = 2000) => {
  try {
    // Start the prediction
    const prediction = await create3DModel(options);

    if (!prediction.id) {
      throw new Error('Invalid prediction ID received from server');
    }

    console.log(`Starting to poll for prediction completion: ${prediction.id}`);

    // Poll for completion
    const result = await pollFor3DModel(prediction.id, pollInterval);

    if (result.status === 'failed') {
      throw new Error(result.error || '3D model generation failed');
    }

    if (result.status !== 'succeeded') {
      throw new Error(`Unexpected prediction status: ${result.status}`);
    }

    return result;
  } catch (error) {
    console.error('Error in generate3DModel:', error);

    // Provide more user-friendly error messages
    if (error.message.includes('Failed to fetch')) {
      throw new Error('Failed to connect to the server. Please check your internet connection.');
    }

    if (error.message.includes('Network Error')) {
      throw new Error('Network error. Please check your connection and try again.');
    }

    if (error.response?.status === 413) {
      throw new Error('The image is too large. Please use a smaller image (max 10MB).');
    }

    if (error.response?.status === 429) {
      throw new Error('Too many requests. Please wait a moment and try again.');
    }

    // Re-throw the original error if no specific handling
    throw error;
  }
};